import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('light');
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize theme from localStorage or default to light mode
  useEffect(() => {
    const storedTheme = localStorage.getItem('lenddisk-theme') as Theme | null;
    
    if (storedTheme) {
      setThemeState(storedTheme);
    } else {
      // Default to light mode
      setThemeState('light');
    }
    
    setIsInitialized(true);
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (isInitialized) {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('lenddisk-theme', theme);
    }
  }, [theme, isInitialized]);

  const toggleTheme = () => {
    setThemeState(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
